/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { colors, mediaQueries, typography, size } from '@everlywell/leaves';
import { Link } from 'gatsby';
import styled, { css } from 'styled-components';

const OpenCartBtn = styled.button`
  font-size: ${size.md}px;
  background: none;
  border: none;
  z-index: 1;
  cursor: pointer;
`;

const CartCloseBtn = styled.button`
  position: relative;
  z-index: 1;
  width: ${size.xl2}px;
  height: ${size.xl2}px;
  transition: transform 0.25s ease-in-out;
  cursor: pointer;
  background: none;
  border: none;
  color: ${colors.gray3};
  &:hover {
    transform: scale(1.1);
  }

  &:focus {
    outline-color: ${colors.teal5};
    outline-style: auto;
  }
`;

const CartInfoWrapper = styled.div`
  height: 100%;
  position: relative;
  z-index: 10001;
  display: flex;
  align-items: center;
  pointer-events: auto;
  color: ${colors.green5};
  border-bottom: ${size.xs2}px solid transparent;
  padding-bottom: 14px;
  margin: 0 auto -15px 0;
  & > svg g path:first-child {
    fill: ${colors.green1};
  }
  &:focus {
    color: ${colors.teal5};
    border-bottom-color: ${colors.teal5};
  }
`;

const CartQuantity = styled.span`
  display: inline-block;
  width: 18px;
  height: 18px;
  color: ${colors.white};
  font-weight: bold;
  font-size: 0.7em;
  text-align: center;
  line-height: 18px;
  border-radius: 50%;
  background-color: ${colors.red3};
  position: absolute;
  right: -7px;
  top: 0;
`;

/* Overlay behind the cart */
interface CartContentOverlayProps {
  showOverlay: boolean | number | undefined;
}

export const CartContentOverlay = styled.div<CartContentOverlayProps>`
  width: ${({ showOverlay }) => (showOverlay ? '100%' : 0)};
  height: 120%; /* hack to cover more area */
  position: fixed;
  z-index: 100000; /* bannersnack is 10000, sleeknote is 99999 */
  top: 0;
  left: 0;
  background-color: ${colors.white};
`;

interface CartContentProps {
  isOpen: boolean;
}

const CartContent = styled.div<CartContentProps>`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;
  position: fixed;
  top: 0;
  right: ${(props) => (props.isOpen ? '0' : '-100%')};
  width: 100%;
  background-color: ${colors.white};
  box-sizing: border-box;
  transition: right 0.2s, width 0.2s;
  ${mediaQueries.forTabletHorizontalUp} {
    box-shadow: -5px 0 ${size.lg}px 0 rgba(0, 0, 0, 0.1);
    width: 375px;
    right: ${(props) => (props.isOpen ? '0' : '-375px')};
  }
`;

export const CartSkeletonWrapper = styled.div`
  display: flex;
  margin-left: ${size.xs1}px;
`;

const CartHeader = styled.div`
  position: relative;
  display: flex;
  flex: 0 0 auto;
  justify-content: space-between;
  padding: ${size.xs2}px ${size.md}px;
  border-bottom: 1px solid ${colors.gray2};
  width: 100%;
  z-index: 3;
  background-color: ${colors.white};
  align-items: center;

  &::after {
    content: '';
    width: 100%;
    height: 20px;
    display: block;
    position: absolute;
    bottom: -21px;
    left: 0;
  }

  ${mediaQueries.forTabletVerticalUp} {
    padding: 22px ${size.md}px;
  }
`;

const CartTitle = styled.div`
  position: absolute;
  left: 0;
  text-align: center;
  width: 100%;
  font-size: 25.6px;
  font-weight: bold;
  line-height: 1.25;
  letter-spacing: 0.5px;

  ${mediaQueries.forTabletVerticalDown} {
    font-size: 22px;
  }
`;

const CartCalcWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-top: ${size.lg}px;
`;

const LabelWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  flex-grow: 1;
`;

const ValueWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  flex-grow: 1;
`;

const EmptyCart = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  font-size: 18px;
  margin-top: ${size.xl3}px;
  line-height: 1.78;
  letter-spacing: 0.7px;
`;

const EmptyCartImg = styled.img`
  width: 114px;
  margin-bottom: ${size.xl1}px;
`;

const CartImage = styled.img`
  width: ${size.xl1}px;
`;

const ShelfContainer = styled.div`
  flex: 1;
  overflow: auto;
  min-height: 280px;
  padding: 0 ${size.lg}px ${size.xl3}px;
`;

const labelText = css`
  font-size: ${size.md}px;
  font-weight: 300;
  line-height: 1.1;
  letter-spacing: 0.62px;
`;

const priceWrapper = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: ${size.md}px 0;
`;

const priceText = css`
  font-family: ${typography.weight.bold};
  font-size: ${size.md}px;
  letter-spacing: 0.62px;
`;

const TotalWrapper = styled.div`
  ${priceWrapper};
`;

const PriceLabel = styled.div`
  ${labelText};
  margin-right: 8px;
  line-height: 2;
`;

const TotalLabel = styled.div`
  ${labelText};
  font-size: 18px;
`;

const Price = styled.div`
  ${priceText};
  line-height: 2;
`;

const TotalPrice = styled.div`
  ${priceText};
  font-size: 20px;
`;

interface AppliedPromoWrapperProps {
  code: string;
}

const AppliedPromoWrapper = styled.div<AppliedPromoWrapperProps>`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: ${(props) =>
    props.code && props.code.length > 11 ? 'wrap' : 'nowrap'};
`;

const DiscountAmount = styled.div`
  ${priceText};
  line-height: 2;
  white-space: nowrap;
  color: ${colors.green5};
`;

const Footer = styled.div`
  position: relative;
  flex: 0 0 auto;
  width: 100%;
  padding: 0 5% 5%;
  background-color: ${colors.white};
  border-top: 1px solid ${colors.gray2};

  &::before {
    content: '';
    width: 100%;
    height: 20px;
    display: block;
    position: absolute;
    top: -21px; /* leave room for top border */
    left: 0;
    background: linear-gradient(rgba(255, 255, 255, 0), rgb(255, 255, 255) 74%);
  }
`;

export interface CheckoutLinkProps {
  disableBtn: boolean;
}

const CheckoutLink = styled(Link)<CheckoutLinkProps>`
  ${(props) => props.disableBtn && 'pointer-events: none'};
  background-color: ${(props) =>
    props.disableBtn ? `${colors.green2}` : `${colors.green5}`};
  border-radius: 1px;
  color: ${colors.white};
  text-align: center;
  font-size: 18.7px;
  font-weight: bold;
  line-height: 1.5;
  letter-spacing: 1px;
  padding: 10px ${size.lg}px;
  text-transform: none;
  transition: background-color 150ms ease-in;
  width: 100%;
  display: flex;
  justify-content: center;
  cursor: pointer;
  text-decoration: none;
  &:hover {
    background-color: ${colors.teal4};
    transition: background-color 150ms ease-in;
  }

  &:focus {
    outline: auto;
  }

  &[disabled] {
    box-shadow: inset 0 1px 5px 0 rgba(0, 0, 0, 0.08);
    background-color: rgba(37, 34, 40, 0.05);
    color: transparent;
    cursor: wait;
    position: relative;
  }

  @keyframes loading {
    0% {
      transform: rotate(0);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  &[disabled]::after {
    animation: 0.5s infinite linear loading;
    border: 0.1rem solid ${colors.green5};
    border-radius: 50%;
    border-right-color: transparent;
    border-top-color: transparent;
    content: '';
    display: block;
    height: 0.8rem;
    left: 50%;
    margin-left: -0.4rem;
    margin-top: -0.4rem;
    position: absolute;
    top: 50%;
    width: 0.8rem;
    z-index: 1;
  }
`;

const PromoWrapper = styled.div`
  width: 100%;
`;

const PromoApplyLink = styled.button`
  color: ${colors.green5};
  ${typography.buttonTextSmall}
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-top: ${size.md}px;
  background-color: transparent;
  border: none;
`;

interface PromoProps {
  shouldShowPromoInput: boolean;
}

const PromoExpandIcon = styled.div<PromoProps>`
  position: relative;
  margin-left: 8px;
  width: ${size.md}px;
  height: ${size.md}px;
  cursor: pointer;
  &::before,
  &::after {
    content: '';
    position: absolute;
    background-color: ${colors.green5};
    transition: transform 0.25s ease-in-out;
  }

  /* Vertical line */
  &::before {
    top: 0;
    left: 50%;
    width: 4px;
    height: 100%;
    margin-left: -2px;
    transform: ${(props) => props.shouldShowPromoInput && 'rotate(90deg)'};
  }

  /* Vertical line */
  &::after {
    top: 50%;
    left: 0;
    width: 100%;
    height: 4px;
    margin-top: -2px;
    transform: ${(props) => props.shouldShowPromoInput && 'rotate(180deg)'};
  }
`;

const InputWrapper = styled.div<PromoProps>`
  overflow: hidden;
  max-height: ${(props) => (props.shouldShowPromoInput ? '84px' : '0')};
  transition: max-height 0.3s ease-in-out;
`;

const Agreement = styled.div`
  font-family: ${typography.weight.light};
  font-size: 14px;
  line-height: 1.5;
  margin-top: 8px;
  text-align: center;

  a {
    color: ${colors.teal4};
    text-decoration: none;
  }
`;

const LoadingScreen = styled.div`
  height: 100%;
  width: 100%;
  background-color: white;
  position: absolute;
  opacity: 0.8;
  z-index: 2;

  @keyframes loading {
    0% {
      transform: rotate(0);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  &::after {
    animation: 0.5s infinite linear loading;
    border: 0.1rem solid ${colors.green5};
    border-radius: 50%;
    border-right-color: transparent;
    border-top-color: transparent;
    content: '';
    display: block;
    height: 2rem;
    left: 50%;
    margin-left: -0.4rem;
    margin-top: -0.4rem;
    position: absolute;
    top: 50%;
    width: 2rem;
  }
`;

export {
  OpenCartBtn,
  CartCloseBtn,
  CartInfoWrapper,
  CartQuantity,
  CartContent,
  CartHeader,
  CartTitle,
  CartCalcWrapper,
  LabelWrapper,
  ValueWrapper,
  ShelfContainer,
  Footer,
  CheckoutLink,
  EmptyCart,
  EmptyCartImg,
  CartImage,
  TotalWrapper,
  TotalLabel,
  TotalPrice,
  PriceLabel,
  Price,
  PromoWrapper,
  PromoApplyLink,
  PromoExpandIcon,
  InputWrapper,
  AppliedPromoWrapper,
  DiscountAmount,
  Agreement,
  LoadingScreen,
};
